import { IUser } from "./IUser";

export interface ILocationLite {
  id?: number;
  facility_name?: string;
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  class_of_trade_id?: number;
  theme_id?: number;
  sub_theme_id?: number;
}

// ===== Payloads =====

// Mirrors `UserWithCustomPassword` in server/src/services/events/users.rs.
// password_hash lives INSIDE the user object for Creation events.
export interface IUserWithCustomPassword extends IUser {
  password_hash?: string;
}

export interface IUserCreationPayload {
  user: IUserWithCustomPassword;
  location?: ILocationLite;
  agreement_pdf_url?: string;
  review_session_id?: number;
  rep_email?: string;
  comments?: string;
  source?: string;
  tags?: number[];
}

export interface IUserUpdatePayload {
  user: IUser;
}

export interface IUserMergePayload {
  dest_email?: string;
  src_email?: string;
  source: string;
  __creator_email?: string;
  __updated_by_email?: string;
}

export interface ICmAssignmentPayload {
  contact_email?: string;
  cm_emails: string[];
  assignment_time: string;
  source: string;
  __creator_email?: string;
  __updated_by_email?: string;
}

export interface IUserLoginStatePayload {
  email?: string;
  source: string;
  __creator_email?: string;
  __updated_by_email?: string;
}

export interface IPasswordUpdatePayload {
  email?: string;
  password_hash: string;
  source: string;
  __creator_email?: string;
  __updated_by_email?: string;
}

// ===== Request envelopes =====

export interface ITestCreationRequest {
  entity: string;
  action: string;
  /** Plain password — server hashes and writes into payload.user.password_hash. */
  password?: string;
  payload: IUserCreationPayload;
}

export interface ITestUpdateRequest {
  entity: string;
  action: string;
  payload: IUserUpdatePayload;
}

export interface ITestMergeRequest {
  entity: string;
  entity_id: number;
  payload: IUserMergePayload;
}

export interface ITestCmAssignmentRequest {
  entity_id: number;
  payload: ICmAssignmentPayload;
}

export interface ITestLoginStateRequest {
  entity: string;
  entity_id: number;
  action: string;
  payload: IUserLoginStatePayload;
}

export interface ITestPasswordUpdateRequest {
  entity: string;
  entity_id: number;
  email?: string;
  password: string;
  source: string;
  __creator_email?: string;
  __updated_by_email?: string;
}

export interface ITestEventResponse {
  event_id: string;
  message: string;
}

export interface ITestEventError {
  error: string;
}

// ===== Action constants — keep in sync with server/src/services/events/mod.rs `mod actions`. =====

export const CREATION_ACTIONS = [
  "signup_reviewed",
  "signup_with_pa",
  "signup_amp",
  "signup_form",
  "signup_survey",
  "signup_nonmed_survey",
] as const;

export const UPDATE_ACTIONS = [
  "profile_updated",
  "distributor_info_updated",
  "alias_set_as_main",
] as const;

export const LOGIN_STATE_ACTIONS = [
  "login_disabled",
  "login_enabled",
  "email_login_disabled",
] as const;

export const PASSWORD_ACTIONS = ["password_updated"] as const;

export type CreationAction = typeof CREATION_ACTIONS[number];
export type UpdateAction = typeof UPDATE_ACTIONS[number];
export type LoginStateAction = typeof LOGIN_STATE_ACTIONS[number];
export type PasswordAction = typeof PASSWORD_ACTIONS[number];
